import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppError, PaymentTypeValues, RestaurantSousTypeValues, RestaurantTypeValues } from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { getIdleRequestStatus, isDsValid } from '@timechef/core/utils/elf-utils';
import { isNullOrUndefined } from '@timechef/core/utils/js-utils';
import { HistoryService } from '@timechef/features/history/state/history.service';
import { RestaurantsRepository, RestaurantsService } from '@timechef/features/restaurants/state';
import { CartFormula, Restaurant } from '@timechef/features/restaurants/state/restaurants.models';
import { map, tap } from 'rxjs';
@UntilDestroy()
@Component({
  selector: 'app-cart-modal',
  templateUrl: './cart-modal.component.html',
  styleUrls: ['./cart-modal.component.scss'],
  standalone: false,
})
export class CartModalComponent implements OnInit, OnDestroy {
  formGroup = this.fb.group({
    deliveryLocation: this.fb.nonNullable.control<number | undefined>(undefined),
    comments: this.fb.nonNullable.control<string | undefined>('', [Validators.maxLength(150)]),
    cgv: this.fb.nonNullable.control(false, [Validators.requiredTrue]),
  });

  commentsCtrl = this.formGroup.get('comments')!;

  checkedCartDS$ = this.restaurantsRepo.checkedCartDS.data$().pipe(
    map((x) => ({ ...x, error: (x.error as AppError) || null })),
    tap((x) => {
      if (isDsValid(x)) {
        const { data } = x;
        if (data && !isNullOrUndefined(this.accountBalance)) {
          if (data.deliveryLocations.length > 0) this.formGroup.controls.deliveryLocation.setValidators([Validators.required]);
          else this.formGroup.controls.deliveryLocation.clearValidators();

          this.accountBalanceWarning =
            this.accountPaymentType === PaymentTypeValues.PrePaiement && !!this.accountBalance && data.totalTTC > this.accountBalance;
          if (data.preferredDeliveryLocationId && data.deliveryLocations.map((x) => x.id).includes(data.preferredDeliveryLocationId)) {
            this.formGroup.patchValue({ deliveryLocation: data.preferredDeliveryLocationId }, { emitEvent: false });
          } else if (data.deliveryLocations.length > 0) this.formGroup.patchValue({ deliveryLocation: data.deliveryLocations[0].id }, { emitEvent: false });
        }
      }
    }),
  );
  accountBalance?: number;
  accountBalanceWarning = false;
  accountPaymentType?: PaymentTypeValues;

  createOrderStatus = getIdleRequestStatus();

  private authRepo: AuthRepository;
  private restaurantsService: RestaurantsService;
  restaurant?: Restaurant;
  RestaurantTypeValues = RestaurantTypeValues;
  RestaurantSousTypeValues = RestaurantSousTypeValues;

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private restaurantsRepo: RestaurantsRepository,
    private historyService: HistoryService,
    private modalCtrl: ModalController,
  ) {
    this.authRepo = injector.get(AuthRepository);
    this.restaurantsService = injector.get(RestaurantsService);
  }

  ngOnInit() {
    this.restaurant = this.restaurantsRepo.getActiveRestaurant();

    this.accountBalance = this.authRepo.getAccountBalance();
    this.accountPaymentType = this.authRepo.getUserPaymentType();
    this.restaurantsService.checkCart().subscribe();
    this.restaurantsRepo.createOrderRequestStatus$.pipe(untilDestroyed(this)).subscribe((st) => {
      this.createOrderStatus = st;
    });

    this.formGroup.controls.deliveryLocation.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value) {
        this.restaurantsRepo.setPreferredDeliveryLocation(+value);
      }
    });
  }

  ngOnDestroy() {
    this.restaurantsRepo.resetCreateOrderStatus();
  }

  createOrder() {
    if (this.formGroup.invalid || this.createOrderStatus.value === 'pending') return;

    const { value } = this.formGroup;

    this.restaurantsService.createOrder(value.deliveryLocation!, value.comments).subscribe(async (result) => {
      if (result) {
        if (result.openModal) await this.historyService.openOrderModal(result.id, { openSingleAsStandalone: true });
        else {
          this.restaurantsService.tryPromptAntiGaspiAlert('first-order');
        }
      }
    });
  }

  async reloadClick() {
    // will be redirected by routerLink, we dismiss cart and menu selection modals
    await this.closeAllModals();
  }

  async openDishDetails(dishId: number) {
    await this.restaurantsService.openDishDetailsModalFromCart(dishId);
  }

  async openFormulaDetails(formula: CartFormula) {
    await this.restaurantsService.openFormulaDetailsModalFromCart(formula);
  }

  async dismissModal() {
    await this.modalCtrl.dismiss();
  }

  async closeAllModals() {
    await this.modalCtrl.dismiss();
    await this.modalCtrl.dismiss();
  }

  deliveryLocDisplayFn = (x: any) => `${x.label}`;
  deliveryLocValueFn = (x: any) => x.id;
}
